import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useStyles } from "../../Styles/styles";
import { UserContext } from "../../Contexts/UserContext";
import { TextField } from "@material-ui/core";
import { styled } from "styled-components";
import Calibration from "./Calibration";
import { useMutation, useQuery } from "@apollo/client";
import { GET_TICKET_SPEED_MEASUREMENT } from "../../Queries/GetTicketSpeedMeasurement";
import { UPDATE_SPEED_MEASUREMENT } from "../../Mutations/IncidentMutations/UpdateSpeedMeasurement";
import { createEmptySpeedMeasurement } from "../../utils/EmptyObjects";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { TICKET_ACTIONS } from "../../Actions/TicketActions";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { parseDateFlexible } from "../../utils/utils";
import { normalizeTicketUpdatePayload } from "../../utils/ticketDispatchUtils";
import moment from "moment";

const StyledHeader = styled.h4`
  width: 100%;
  text-align: center;
  color: ${props => (props.$nightMode ? "white" : "black")};
  margin: 0;
`;

const BorderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  border: 1px solid grey;
  width: 100%;
  margin: 10px;
`;

const FlexColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: ${props => props.$flexGrow || "unset"};
`;

const SpeedMeasurement = ({
  locked = true,
  userRoles,
  inIncident,
  ticketId,
  incidentId,
  setPausePolling,
  ...props
}) => {
  const { nightMode, user, onlineState } = useContext(UserContext);
  const { incidents, incidentDispatch, ticketDispatch } =
    useContext(IncidentsContext);
  const { UPDATE_INCIDENT_TICKET } = INCIDENT_ACTIONS;
  const { UPDATE_TICKET } = TICKET_ACTIONS;

  const [localData, setLocalData] = useState(null);
  //this will be used to update the entire speedMeasurement object in the incident context
  const localDataRef = useRef({});
  const [delta, setDelta] = useState({});
  //deltaRef will be used to track changes in delta for the final unmounting event. Otherwise delta will be an empty object due to the useEffect being fired on mount and delta being an empty {}
  const deltaRef = useRef({});
  const inactivityTimeout = useRef(null);

  const { data, loading } = useQuery(GET_TICKET_SPEED_MEASUREMENT, {
    context: { serializationKey: "incidentQueue" },
    variables: { ticketId: ticketId, originatorId: user?.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      //when this returns make sure there isn't already local data (eg offline while creating the SM)
      if (!localData) {
        if (data.getTicket.speedMeasurement) {
          setLocalData(data.getTicket.speedMeasurement);
        } else {
          const emptySM = createEmptySpeedMeasurement();
          setLocalData(emptySM);
        }
      }
    },
  });

  const [updateTicketSpeedMeasurement] = useMutation(UPDATE_SPEED_MEASUREMENT, {
    context: { serializationKey: "incidentQueue" },
    variables: {
      input: {
        ticketId: ticketId,
        speedMeasurementId: localData?.id,
        originatorId: user?.id,
        ...delta,
      },
    },
  });

  const handleDispatch = useCallback(
    (inIncidentAction, outOfIncidentAction, payload) => {
      const nextPayload =
        inIncidentAction === UPDATE_INCIDENT_TICKET ||
        outOfIncidentAction === UPDATE_TICKET
          ? normalizeTicketUpdatePayload(payload)
          : payload;
      if (inIncident) {
        incidentDispatch({ type: inIncidentAction, payload: nextPayload });
      } else {
        ticketDispatch({ type: outOfIncidentAction, payload: nextPayload });
      }
      return null;
    },
    [
      inIncident,
      incidentDispatch,
      ticketDispatch,
      UPDATE_INCIDENT_TICKET,
      UPDATE_TICKET,
    ],
  );

  //keeps the deltaRef updated from changes in delta
  useEffect(() => {
    deltaRef.current = delta;
  }, [delta]);

  useEffect(() => {
    localDataRef.current = localData;
  }, [localData]);

  useEffect(() => {
    if (!onlineState && !data && !localData) {
      //first check if data exists
      const matchesId = (entity, id) =>
        [entity?.id, entity?.uuid].some(
          value => value != null && String(value) === String(id),
        );
      const incidentMatch = incidents.find(i => matchesId(i, incidentId));
      const ticketMatch = incidentMatch?.incidentTickets?.find(t =>
        matchesId(t, ticketId),
      );
      let offlineSpeedMeasurement;
      if (ticketMatch?.speedMeasurement) {
        offlineSpeedMeasurement = ticketMatch.speedMeasurement;
      } else {
        offlineSpeedMeasurement = createEmptySpeedMeasurement();
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId,
          ticketId,
          property: "speedMeasurement",
          value: offlineSpeedMeasurement,
        });
      }
      setLocalData(offlineSpeedMeasurement);
    }
  }, [
    onlineState,
    data,
    incidents,
    incidentId,
    ticketId,
    localData,
    handleDispatch,
    UPDATE_INCIDENT_TICKET,
    UPDATE_TICKET,
  ]);

  const classes = useStyles();

  const handleDataUpdate = (field, value, type) => {
    const currentLocalData = localDataRef.current || {};

    if (type === "date") {
      const parsedDate = parseDateFlexible(value);
      if (moment.isMoment(value)) {
        const serverTimeFormat = parsedDate;
        value = serverTimeFormat;
      } else {
        value = "";
      }
    }
    if (currentLocalData[field] !== value) {
      setLocalData(prevData => ({ ...(prevData || {}), [field]: value }));
      setDelta(prevDelta => getFilteredDelta(field, value, prevDelta));
    }

    // Reset and start the inactivity timer again
    clearTimeout(inactivityTimeout.current);
    inactivityTimeout.current = setTimeout(() => {
      fireMutation();
    }, 10000); // 10 seconds
  };

  const fireMutation = useCallback(() => {
    if (Object.keys(deltaRef.current).length > 0) {
      //this will update the IncidentContext with the entire speedMeasurement object.
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId,
        ticketId,
        property: "speedMeasurement",
        value: localDataRef.current,
      });
      updateTicketSpeedMeasurement();
      setDelta({}); // Reset delta after mutation
    }

    // Clear the inactivity timer
    clearTimeout(inactivityTimeout.current);
  }, [
    UPDATE_INCIDENT_TICKET,
    UPDATE_TICKET,
    handleDispatch,
    incidentId,
    ticketId,
    updateTicketSpeedMeasurement,
  ]);

  useEffect(() => {
    return () => {
      // This function will be executed when the component is unmounted
      clearTimeout(inactivityTimeout.current); // Clear any existing timeout
      fireMutation(); // Fire the mutation if there's any pending data
    };
  }, [fireMutation]);

  //if a user changes a value, then changes it back, this function will remove the "change" (which is now the original value) from the delta object
  const getFilteredDelta = (key, value, prevDelta) => {
    const newDelta = { ...prevDelta, [key]: value };

    // Check against the original data
    if (
      data &&
      data.getTicket &&
      data.getTicket.speedMeasurement &&
      data.getTicket.speedMeasurement[key] === value
    ) {
      delete newDelta[key];
    }

    return newDelta;
  };

  const beforeRadarData = [
    {
      stateName: "radarTuningForksBefore",
      value: localData?.radarTuningForksBefore,
      helperText: "Tuning",
    },
    {
      stateName: "radarInternalCalibrationBefore",
      value: localData?.radarInternalCalibrationBefore,
      helperText: "Calibration",
    },
    {
      stateName: "radarSpeedometerVerifBefore",
      value: localData?.radarSpeedometerVerifBefore,
      helperText: "Speedometer",
    },
    {
      stateName: "radarDisplayLightsBefore",
      value: localData?.radarDisplayLightsBefore,
      helperText: "Display Lights",
    },
  ];
  const beforeLaserData = [
    {
      stateName: "laserSelfBefore",
      value: localData?.laserSelfBefore,
      helperText: "Self",
    },
    {
      stateName: "laserScopeAlignmentBefore",
      value: localData?.laserScopeAlignmentBefore,
      helperText: "Scope",
    },
    {
      stateName: "laserKnownDistanceBefore",
      value: localData?.laserKnownDistanceBefore,
      helperText: "Distance",
    },
    {
      stateName: "laserDisplayLightsBefore",
      value: localData?.laserDisplayLightsBefore,
      helperText: "Display Lights",
    },
  ];
  const afterRadarData = [
    {
      stateName: "radarTuningForksAfter",
      value: localData?.radarTuningForksAfter,
      helperText: "Tuning",
    },
    {
      stateName: "radarInternalCalibrationAfter",
      value: localData?.radarInternalCalibrationAfter,
      helperText: "Calibration",
    },
    {
      stateName: "radarSpeedometerVerifAfter",
      value: localData?.radarSpeedometerVerifAfter,
      helperText: "Speedometer",
    },
    {
      stateName: "radarDisplayLightsAfter",
      value: localData?.radarDisplayLightsAfter,
      helperText: "Display Lights",
    },
  ];
  const afterLaserData = [
    {
      stateName: "laserSelfAfter",
      value: localData?.laserSelfAfter,
      helperText: "Self",
    },
    {
      stateName: "laserScopeAlignmentAfter",
      value: localData?.laserScopeAlignmentAfter,
      helperText: "Scope",
    },
    {
      stateName: "laserKnownDistanceAfter",
      value: localData?.laserKnownDistanceAfter,
      helperText: "Distance",
    },
    {
      stateName: "laserDisplayLightsAfter",
      value: localData?.laserDisplayLightsAfter,
      helperText: "Display Lights",
    },
  ];

  if (onlineState && loading) return <p>Loading...</p>;

  return (
    <>
      <StyledHeader $nightMode={nightMode}>
        Speed Measurement Information
      </StyledHeader>
      <BorderContainer>
        <FlexColumnContainer>
          <TextField
            disabled={locked}
            data-cy="radarIdStr"
            inputProps={{ "data-cy": "radarIdStr-input" }}
            InputLabelProps={{
              "data-cy": "radarIdStr-input-label",
              shrink: true,
            }}
            margin="dense"
            id="radarIdStr-id"
            label={"Radar\xa0ID"}
            value={localData?.radarIdStr || ""}
            name="radarIdStr"
            className={classes.textInput}
            onChange={e => handleDataUpdate(e.target.name, e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
          <TextField
            disabled={locked}
            data-cy="laserIdStr"
            inputProps={{ "data-cy": "laserIdStr-input" }}
            InputLabelProps={{
              "data-cy": "laserIdStr-input-label",
              shrink: true,
            }}
            margin="dense"
            id="laserIdStr-id"
            label={"Laser\xa0ID"}
            value={localData?.laserIdStr || ""}
            name="laserIdStr"
            className={classes.textInput}
            onChange={e => handleDataUpdate(e.target.name, e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
        </FlexColumnContainer>
        <FlexColumnContainer $flexGrow={1}>
          <Calibration
            id="radar-before-stop"
            locked={locked}
            dateValue={localData?.radarTimeBefore}
            dateName={"radarTimeBefore"}
            handleChange={handleDataUpdate}
            userRoles={userRoles}
            title="Before Stop"
            dropDownArr={beforeRadarData}
            setPausePolling={setPausePolling}
            {...props}
          />
          <Calibration
            id="laser-before-stop"
            locked={locked}
            dateValue={localData?.laserTimeBefore}
            dateName="laserTimeBefore"
            handleChange={handleDataUpdate}
            userRoles={userRoles}
            dropDownArr={beforeLaserData}
            setPausePolling={setPausePolling}
            {...props}
          />
        </FlexColumnContainer>
        <FlexColumnContainer $flexGrow={1}>
          <Calibration
            id="radar-after-stop"
            locked={locked}
            dateValue={localData?.radarTimeAfter}
            dateName={"radarTimeAfter"}
            handleChange={handleDataUpdate}
            userRoles={userRoles}
            title="After Stop"
            dropDownArr={afterRadarData}
            setPausePolling={setPausePolling}
            {...props}
          />
          <Calibration
            id="laser-after-stop"
            locked={locked}
            dateValue={localData?.laserTimeAfter}
            dateName="laserTimeAfter"
            handleChange={handleDataUpdate}
            userRoles={userRoles}
            dropDownArr={afterLaserData}
            setPausePolling={setPausePolling}
            {...props}
          />
        </FlexColumnContainer>
      </BorderContainer>
    </>
  );
};

export default SpeedMeasurement;
